import { fetchRouteElevation, enrichCoordinatesWithElevation } from './realElevationAPI';
import { fetchRouteViaBackend } from './backendRoutingAPI';

/**
 * Main route fetching function - REAL DATA ONLY
 * @param {object} start - Start coordinates { lat, lng }
 * @param {object} destination - Destination coordinates { lat, lng }
 * @param {string} profile - Route profile: 'driving-car', 'walking', etc.
 * @param {number} alternatives - Number of alternative routes (0 = single route only)
 * @returns {Promise<array>} Array with single route data (real data only)
 */
export const fetchRoute = async (start, destination, profile = 'driving-car', alternatives = 0) => {
  // Fetch REAL elevation data first
  let realElevationData = null;
  try {
    console.log('🌍 Fetching REAL elevation data from OpenTopography via backend...');
    realElevationData = await fetchRouteElevation(start, destination);
    console.log('✅ Real elevation data loaded!', realElevationData.source);
  } catch (error) {
    console.error('❌ Failed to fetch elevation data:', error.message);
    throw new Error(`Elevation data fetch failed: ${error.message}`);
  }

  // Fetch route from backend (no alternatives)
  let routes;
  try {
    console.log('🗺️  Fetching route via backend proxy...');
    routes = await fetchRouteViaBackend(start, destination, profile, alternatives);
    
    if (!routes || routes.length === 0) {
      throw new Error('No routes returned from routing service');
    }
    
    console.log(`✅ Got ${routes.length} route(s) from backend`);
  } catch (error) {
    console.error('❌ Routing failed:', error.message);
    throw new Error(`Route fetch failed: ${error.message}`);
  }

  // Enrich routes with REAL elevation data
  routes = routes.map(route => ({
    ...route,
    coordinates: enrichCoordinatesWithElevation(route.coordinates, realElevationData),
    elevationData: calculateElevationProfile(enrichCoordinatesWithElevation(route.coordinates, realElevationData)),
    hasRealElevation: true,
    rawElevationData: realElevationData  // Store raw elevation grid for later analysis
  }));
  
  console.log('✅ Route enriched with REAL SRTM elevation data');
  console.log(`✅ Returning ${routes.length} real route(s)`);
  
  return routes;
};

/**
 * Calculate elevation profile data
 * @param {array} coordinates - Route coordinates with elevation
 * @returns {object} Elevation statistics
 */
const calculateElevationProfile = (coordinates) => {
  let totalAscent = 0;
  let totalDescent = 0;
  let maxElevation = -Infinity;
  let minElevation = Infinity;

  for (let i = 1; i < coordinates.length; i++) {
    const elevDiff = coordinates[i].elevation - coordinates[i - 1].elevation;
    if (elevDiff > 0) totalAscent += elevDiff;
    if (elevDiff < 0) totalDescent += Math.abs(elevDiff);

    maxElevation = Math.max(maxElevation, coordinates[i].elevation);
    minElevation = Math.min(minElevation, coordinates[i].elevation);
  }

  return {
    totalAscent: Math.round(totalAscent),
    totalDescent: Math.round(totalDescent),
    maxElevation: Math.round(maxElevation),
    minElevation: Math.round(minElevation),
    elevationGain: Math.round(maxElevation - minElevation)
  };
};

/**
 * Calculate route statistics for display
 * @param {object} route - Route data
 * @returns {object} Statistics
 */
export const calculateRouteStats = (route) => {
  const distanceKm = (route.distance / 1000).toFixed(2);
  const durationHours = Math.floor(route.duration / 3600);
  const durationMinutes = Math.floor((route.duration % 3600) / 60);

  return {
    distance: `${distanceKm} km`,
    duration: `${durationHours}h ${durationMinutes}m`,
    ascent: route.ascent ? `${route.ascent}m` : 'N/A',
    descent: route.descent ? `${route.descent}m` : 'N/A',
    elevationGain: route.elevationData?.elevationGain ? `${route.elevationData.elevationGain}m` : 'N/A',
    maxElevation: route.elevationData?.maxElevation ? `${route.elevationData.maxElevation}m` : 'N/A',
    minElevation: route.elevationData?.minElevation ? `${route.elevationData.minElevation}m` : 'N/A'
  };
};

/**
 * Get route color based on route index
 * @param {number} routeIndex - Index of route (0 = main)
 * @returns {string} Color hex code
 */
export const getRouteColor = (routeIndex) => {
  const colors = [
    '#0066cc', // Blue - main route
    '#cc6600', // Orange - alternative 1
    '#00cc66'  // Green - alternative 2
  ];
  return colors[routeIndex] || '#666666';
};

/**
 * Available route profiles
 */
export const ROUTE_PROFILES = {
  DRIVING_CAR: 'driving-car',
  DRIVING_HGV: 'driving-hgv', // Heavy goods vehicle
  FOOT_WALKING: 'foot-walking',
  FOOT_HIKING: 'foot-hiking'
};
